import React, { useEffect, useRef, useState } from 'react'

import showToastNotification from '../widgets/toastify'
import {
  fetchUserConcurrencyLimit,
  fetchOrgConcurrencyLimit,
  updateUserConcurrencyLimit,
  updateOrgConcurrencyLimit,
} from './helpers/concurrency_limit_service'

const initialState = {
  lookup: null,
  minValue: '',
  maxValue: '',
  reason: '',
  isLoading: false,
  isSaving: false,
  error: null,
}

/* Single-scope (user or org) lookup/edit card for one entity's Data Pulse concurrency min/max --
 * reused once per scope by ConcurrencyLimitEditor so both columns render side by side. Auto-fetches
 * off entityId instead of a manual lookup button, since the id now always comes from EntitySearch. */
const ConcurrencyLimitColumn = ({ client, scope, entityId, operatorId, disabled, disabledMessage }) => {
  const [state, setState] = useState(initialState)
  const { lookup, minValue, maxValue, reason, isLoading, isSaving, error } = state

  const abortControllerRef = useRef(null)

  useEffect(() => {
    if (!entityId || disabled) {
      setState(initialState)
      return undefined
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    const controller = new AbortController()
    abortControllerRef.current = controller

    setState((prev) => ({ ...prev, isLoading: true, error: null }))
    const fetcher = scope === 'user'
      ? fetchUserConcurrencyLimit(client, { userId: entityId }, controller.signal)
      : fetchOrgConcurrencyLimit(client, { orgId: entityId }, controller.signal)

    fetcher
      .then((data) => {
        if (abortControllerRef.current !== controller) return
        if (!data) {
          setState((prev) => ({ ...prev, error: 'Could not look up limits for this ' + scope + '.', lookup: null }))
          return
        }

        // Seed only from lookup.configured -- default_min/pool_size are resolved fallbacks, not a
        // saved override, and must never be shown as an editable "current" value.
        setState((prev) => ({
          ...prev,
          lookup: data,
          minValue: data.configured ? String(data.configured.min) : '',
          maxValue: data.configured ? String(data.configured.max) : '',
        }))
      })
      .catch(() => {
        if (abortControllerRef.current !== controller) return
        setState((prev) => ({ ...prev, error: 'Could not look up limits for this ' + scope + '.', lookup: null }))
      })
      .finally(() => {
        if (abortControllerRef.current === controller) setState((prev) => ({ ...prev, isLoading: false }))
      })

    return () => controller.abort()
  }, [client, entityId, scope, disabled])

  const onSave = () => {
    const min = Number(minValue)
    const max = Number(maxValue)
    if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) {
      setState((prev) => ({ ...prev, error: 'Min and max must be numbers, with min <= max.' }))
      return
    }

    if (!reason.trim()) {
      setState((prev) => ({ ...prev, error: 'A reason is required.' }))
      return
    }

    setState((prev) => ({ ...prev, error: null, isSaving: true }))
    const concurrencyLimits = { min, max }
    const action = scope === 'user'
      ? updateUserConcurrencyLimit(client, { coUserId: entityId, operatorId, concurrencyLimits, reason: reason.trim() })
      : updateOrgConcurrencyLimit(client, { coOrgId: entityId, operatorId, concurrencyLimits, reason: reason.trim() })

    action
      .then((data) => {
        showToastNotification({ message: 'Concurrency limits updated.', isSuccess: true })
        const saved = (data && data.value && data.value.data_pulse && data.value.data_pulse.realtime) || concurrencyLimits
        setState((prev) => ({ ...prev, lookup: { ...prev.lookup, configured: saved }, reason: '' }))
      })
      .catch((err) => {
        showToastNotification({ message: (err && err.message) || 'Could not update limits.', isSuccess: false })
      })
      .finally(() => setState((prev) => ({ ...prev, isSaving: false })))
  }

  // One stable root, modifier class + conditional children instead of three separate early
  // returns -- keeps this in line with entity_search.js's fix for the removeChild crash (a
  // full-tree swap risks React losing track of the DOM across an unmount+mount in one commit).
  const isInert = disabled || !entityId

  return (
    <div className={'dpfu-editor-card' + (isInert ? ' dpfu-editor-card--disabled' : '')}>
      {disabled ? (
        <p className='dpfu-editor-disabled-message'>{disabledMessage}</p>
      ) : !entityId ? (
        <p className='dpfu-editor-disabled-message'>Select a user above.</p>
      ) : (
        <>
          {isLoading && !lookup && <p className='dpfu-editor-hint'>Looking up…</p>}
          {error && <p className='dpfu-editor-error'>{error}</p>}
          {lookup && (
            <>
              <p className='dpfu-editor-hint'>
                {lookup.configured
                  ? 'Configured override in place.'
                  : 'No explicit override — currently defaults to min ' + lookup.default_min + ' / max ' + lookup.pool_size + '.'}
              </p>
              <div className='dpfu-editor-form'>
                <div className='dpfu-editor-row'>
                  <div className='dpfu-editor-field'>
                    <label>Min concurrency</label>
                    <input
                      type='number'
                      className='dpfu-editor-input dpfu-editor-input--num'
                      value={minValue}
                      onChange={(e) => setState((prev) => ({ ...prev, minValue: e.target.value }))}
                    />
                  </div>
                  <div className='dpfu-editor-field'>
                    <label>Max concurrency</label>
                    <input
                      type='number'
                      className='dpfu-editor-input dpfu-editor-input--num'
                      value={maxValue}
                      onChange={(e) => setState((prev) => ({ ...prev, maxValue: e.target.value }))}
                    />
                  </div>
                </div>
                <div className='dpfu-editor-row'>
                  <div className='dpfu-editor-field dpfu-editor-field--grow'>
                    <label>Reason</label>
                    <textarea
                      className='dpfu-editor-input dpfu-editor-input--reason'
                      placeholder='Why are you changing this limit?'
                      value={reason}
                      onChange={(e) => setState((prev) => ({ ...prev, reason: e.target.value }))}
                    />
                  </div>
                </div>
                <div className='dpfu-editor-actions'>
                  <button
                    type='button'
                    className='dpfu-editor-btn dpfu-editor-btn--primary'
                    disabled={isSaving}
                    onClick={onSave}
                  >
                    {isSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

export default ConcurrencyLimitColumn
